/**
 * Gas: cost computation, the auto-pay algorithm and affordability feedback.
 * RULES section 5.
 */

import type { Chain, Cost } from './cardTypes';
import { profileOf } from './cardDb';
import { collectStatics, matchesFilter, profile } from './query';
import { CHAINS, type GameState, type GasPool } from './types';

/** Gas symbols per chain, used only for player-facing reason strings. */
export const GAS_SYMBOL: Record<Chain | 'generic', string> = {
  generic: 'generic Gas',
  btc: '₿',
  eth: '◆',
  sol: '⚡',
  bnb: '⬢',
  xrp: '◎',
};

export function costTotal(cost: Cost): number {
  return (
    (cost.generic ?? 0) +
    (cost.btc ?? 0) +
    (cost.eth ?? 0) +
    (cost.sol ?? 0) +
    (cost.bnb ?? 0) +
    (cost.xrp ?? 0)
  );
}

function factionPart(cost: Cost): number {
  return costTotal({ ...cost, generic: 0 });
}

/**
 * The printed cost of a card in a zone, with `costModifier` statics applied.
 * Modifiers touch the generic part only and never reduce the total below the
 * faction part (RULES 5).
 */
export function computeCost(
  state: GameState,
  instanceId: string,
  extraGeneric = 0,
): Cost {
  const o = state.objects[instanceId];
  if (!o) return {};
  const printed = profileOf(o).cost;
  const base: Cost = {
    generic: (printed.generic ?? 0) + Math.max(0, extraGeneric),
    btc: printed.btc ?? 0,
    eth: printed.eth ?? 0,
    sol: printed.sol ?? 0,
    bnb: printed.bnb ?? 0,
    xrp: printed.xrp ?? 0,
  };

  let delta = 0;
  for (const st of collectStatics(state)) {
    if (st.effect.type !== 'costModifier') continue;
    if (!matchesFilter(state, o, st.effect.filter, st.effect.who, st.controller, st.source.instanceId)) {
      continue;
    }
    delta += st.effect.amount;
  }

  base.generic = Math.max(0, (base.generic ?? 0) + delta);
  return base;
}

// ---------------------------------------------------------------------------
// Payment planning
// ---------------------------------------------------------------------------

export interface PaymentPlan {
  ok: boolean;
  /** Player-facing reason when ok is false. */
  reason?: string;
  /** Gas taken out of the pool, per bucket. */
  poolSpend: GasPool;
  /** Wallet instance ids to exhaust, in order. */
  walletsToTap: string[];
  /** Gas produced by tapping that is not spent and stays in the pool. */
  leftover: GasPool;
}

interface WalletInfo {
  instanceId: string;
  chain: Chain | 'any';
  produces: number;
}

function readyWallets(state: GameState, playerId: string): WalletInfo[] {
  const out: WalletInfo[] = [];
  for (const id of state.players[playerId].battlefield.wallets) {
    const o = state.objects[id];
    if (!o || o.zone !== 'battlefield' || o.exhausted) continue;
    const w = profileOf(o).wallet;
    if (!w) continue;
    out.push({ instanceId: id, chain: w.chain, produces: w.produces ?? 1 });
  }
  return out;
}

function zeroPool(): GasPool {
  return { generic: 0, btc: 0, eth: 0, sol: 0, bnb: 0, xrp: 0 };
}

/**
 * How badly each chain is needed by the other cards in hand. Used to pick which
 * Wallet to exhaust for a generic requirement.
 */
function chainNeedFromHand(state: GameState, playerId: string, excludeInstanceId: string): Record<Chain, number> {
  const need: Record<Chain, number> = { btc: 0, eth: 0, sol: 0, bnb: 0, xrp: 0 };
  for (const id of state.players[playerId].hand) {
    if (id === excludeInstanceId) continue;
    const cost = profile(state, id).cost;
    for (const c of CHAINS) need[c] += cost[c] ?? 0;
  }
  return need;
}

/**
 * Work out exactly which pool gas and which Wallets pay `cost`, deterministically.
 *
 * Order (RULES 5):
 * 1. Pool gas is spent before any Wallet is exhausted.
 * 2. Faction requirements are paid from matching gas and matching Wallets.
 * 3. Generic is paid from leftover gas, then from Wallets whose chain is least
 *    needed by the other cards in hand, ties broken by the most plentiful chain.
 */
export function planPayment(
  state: GameState,
  playerId: string,
  cost: Cost,
  excludeInstanceId = '',
): PaymentPlan {
  const remaining: Record<Chain | 'generic', number> = {
    generic: cost.generic ?? 0,
    btc: cost.btc ?? 0,
    eth: cost.eth ?? 0,
    sol: cost.sol ?? 0,
    bnb: cost.bnb ?? 0,
    xrp: cost.xrp ?? 0,
  };

  const pool = { ...state.players[playerId].gas };
  const poolSpend = zeroPool();
  const walletsToTap: string[] = [];
  /** Gas produced by tapping, not yet spent. */
  const produced = zeroPool();

  // 1 and 2: faction requirements from pool gas of that chain.
  for (const c of CHAINS) {
    if (remaining[c] <= 0) continue;
    const take = Math.min(pool[c], remaining[c]);
    pool[c] -= take;
    poolSpend[c] += take;
    remaining[c] -= take;
  }

  const wallets = readyWallets(state, playerId);
  const used = new Set<string>();

  // Faction requirements from matching Wallets.
  for (const c of CHAINS) {
    while (remaining[c] > 0) {
      const w = wallets.find((x) => !used.has(x.instanceId) && x.chain === c);
      if (!w) {
        return {
          ok: false,
          reason: `Needs ${remaining[c]} more ${GAS_SYMBOL[c]}`,
          poolSpend,
          walletsToTap,
          leftover: produced,
        };
      }
      used.add(w.instanceId);
      walletsToTap.push(w.instanceId);
      produced[c] += w.produces;
      const take = Math.min(produced[c], remaining[c]);
      produced[c] -= take;
      remaining[c] -= take;
    }
  }

  // 3: generic from leftover pool gas, generic bucket first then chain gas.
  if (remaining.generic > 0) {
    const takeGeneric = Math.min(pool.generic, remaining.generic);
    pool.generic -= takeGeneric;
    poolSpend.generic += takeGeneric;
    remaining.generic -= takeGeneric;
  }
  if (remaining.generic > 0) {
    for (const c of CHAINS) {
      if (remaining.generic <= 0) break;
      const take = Math.min(produced[c], remaining.generic);
      produced[c] -= take;
      remaining.generic -= take;
    }
  }
  if (remaining.generic > 0) {
    for (const c of CHAINS) {
      if (remaining.generic <= 0) break;
      const take = Math.min(pool[c], remaining.generic);
      pool[c] -= take;
      poolSpend[c] += take;
      remaining.generic -= take;
    }
  }

  // Generic from Wallets, least-needed chain first.
  if (remaining.generic > 0) {
    const need = chainNeedFromHand(state, playerId, excludeInstanceId);
    const plentiful: Record<Chain, number> = { btc: 0, eth: 0, sol: 0, bnb: 0, xrp: 0 };
    for (const w of wallets) {
      if (w.chain !== 'any') plentiful[w.chain]++;
    }
    const candidates = wallets
      .filter((w) => !used.has(w.instanceId))
      .sort((a, b) => {
        // Chain-agnostic Wallets can only pay generic, so spend them first.
        const aAny = a.chain === 'any' ? 0 : 1;
        const bAny = b.chain === 'any' ? 0 : 1;
        if (aAny !== bAny) return aAny - bAny;
        const aNeed = a.chain === 'any' ? -1 : need[a.chain];
        const bNeed = b.chain === 'any' ? -1 : need[b.chain];
        if (aNeed !== bNeed) return aNeed - bNeed;
        const aPlenty = a.chain === 'any' ? 0 : plentiful[a.chain];
        const bPlenty = b.chain === 'any' ? 0 : plentiful[b.chain];
        if (aPlenty !== bPlenty) return bPlenty - aPlenty;
        return a.instanceId < b.instanceId ? -1 : 1;
      });

    for (const w of candidates) {
      if (remaining.generic <= 0) break;
      used.add(w.instanceId);
      walletsToTap.push(w.instanceId);
      const bucket: Chain | 'generic' = w.chain === 'any' ? 'generic' : w.chain;
      produced[bucket] += w.produces;
      const take = Math.min(produced[bucket], remaining.generic);
      produced[bucket] -= take;
      remaining.generic -= take;
    }
  }

  if (remaining.generic > 0) {
    return {
      ok: false,
      reason: `Needs ${remaining.generic} more ${GAS_SYMBOL.generic}`,
      poolSpend,
      walletsToTap,
      leftover: produced,
    };
  }

  return { ok: true, poolSpend, walletsToTap, leftover: produced };
}

// ---------------------------------------------------------------------------
// Affordability
// ---------------------------------------------------------------------------

export interface Affordability {
  affordable: boolean;
  reason?: string;
  cost: Cost;
}

/** Can this player pay for the card in this instance right now? */
export function canAfford(state: GameState, playerId: string, instanceId: string, xValue = 0): Affordability {
  const prof = profile(state, instanceId);
  const cost = computeCost(state, instanceId, prof.cost.x ? xValue : 0);
  const plan = planPayment(state, playerId, cost, instanceId);
  if (plan.ok) return { affordable: true, cost };
  return { affordable: false, reason: plan.reason, cost };
}

/** Total gas a player could produce right now, for bot heuristics. */
export function availableGas(state: GameState, playerId: string): GasPool {
  const pool = { ...state.players[playerId].gas };
  for (const w of readyWallets(state, playerId)) {
    const bucket: Chain | 'generic' = w.chain === 'any' ? 'generic' : w.chain;
    pool[bucket] += w.produces;
  }
  return pool;
}

export { factionPart };
